import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, Send, Upload, X } from "lucide-react";
import toast from "react-hot-toast";

import {
  collectBrowserInfo,
  createErrorReport,
  getMondayOptions,
} from "../api/errorReportsApi";

const BRAND = "#55C5D0";
const MAX_FILES = 10;
const MAX_FILE_MB = 10;
const ACCEPT = "image/*,.pdf,.csv,.txt,.doc,.docx,.xls,.xlsx,.mp4";

// Fixed severity order regardless of what order Monday's board returns the
// labels in. Anything the board has that isn't in this list still shows up,
// just tacked on at the end rather than silently dropped.
const PRIORITY_ORDER = ["Low", "Medium", "High", "Critical"];
const sortPriorities = (priorities) => {
  const known = PRIORITY_ORDER.filter((p) =>
    priorities.some((live) => live.toLowerCase() === p.toLowerCase())
  ).map((p) => priorities.find((live) => live.toLowerCase() === p.toLowerCase()));
  const rest = priorities.filter((live) => !known.includes(live));
  return [...known, ...rest];
};

/**
 * Report an issue, from anywhere in the CRM.
 *
 * Deliberately a modal rather than its own page: a bug gets reported while the
 * person is looking at it, so opening in place lets us capture the exact page,
 * browser and screen size automatically. A standalone page would only ever
 * record its own URL, which tells nobody anything.
 *
 * Board, group, priority and status are all populated live from Monday, so a
 * label the client renames on the board is reflected here with no deploy.
 */
const ReportIssueModal = ({ open, onClose, onSubmitted }) => {
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dropActive, setDropActive] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    boardId: "",
    groupId: "",
    priority: "",
    status: "",
    assigneeId: "",
  });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  // The page they were on when they hit the button. Captured on open, because by
  // the time they submit the modal is the only thing on screen.
  const pageUrlRef = useRef("");
  const previewsRef = useRef([]);
  previewsRef.current = previews;

  const loadOptions = useCallback(async (boardId) => {
    setLoadingOptions(true);
    try {
      const data = await getMondayOptions(boardId);
      setOptions(data);
      setForm((prev) => ({
        ...prev,
        boardId: data.board_id || data.default_board_id || "",
        // Area and Priority default to the "--Select--" placeholder rather
        // than an auto-picked value -- both are required, so the reporter has
        // to make a deliberate choice instead of accidentally submitting
        // whatever Monday's first group/default priority happened to be.
        groupId: "",
        priority: "",
        status: data.default_status || "",
        // Assignee stays empty by default, so an unassigned report lands with
        // whoever triages the board rather than being pinned on someone.
        assigneeId: "",
      }));
      if (data.error) toast.error(data.error);
    } catch (e) {
      // Not fatal: they can still submit and the server applies its defaults.
      toast.error(
        e?.response?.data?.detail ||
          "Could not load the Monday options. You can still submit."
      );
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    pageUrlRef.current = window.location.href;
    setForm({
      title: "",
      description: "",
      boardId: "",
      groupId: "",
      priority: "",
      status: "",
      assigneeId: "",
    });
    setFiles([]);
    setPreviews([]);
    loadOptions();
  }, [open, loadOptions]);

  // Release the object URLs when the modal closes, or they leak for the session.
  useEffect(
    () => () => previewsRef.current.forEach((url) => URL.revokeObjectURL(url)),
    []
  );

  const changeBoard = (boardId) => {
    setForm((prev) => ({ ...prev, boardId }));
    loadOptions(boardId);
  };

  const acceptFiles = useCallback(
    (incoming) => {
      const list = Array.from(incoming || []);
      if (!list.length) return;

      const kept = [];
      for (const f of list) {
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
          toast.error(`"${f.name}" is over ${MAX_FILE_MB} MB and was skipped.`);
          continue;
        }
        kept.push(f);
      }
      setFiles((prev) => {
        const room = MAX_FILES - prev.length;
        if (room <= 0) {
          toast.error(`You can attach up to ${MAX_FILES} files.`);
          return prev;
        }
        const take = kept.slice(0, room);
        if (kept.length > room) {
          toast.error(`Only the first ${room} file(s) were added (limit ${MAX_FILES}).`);
        }
        setPreviews((p) => [
          ...p,
          ...take.map((f) =>
            f.type?.startsWith("image/") ? URL.createObjectURL(f) : ""
          ),
        ]);
        return [...prev, ...take];
      });
    },
    []
  );

  const removeFile = (idx) => {
    if (previews[idx]) URL.revokeObjectURL(previews[idx]);
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // Paste a screenshot straight in. On Windows this is how people actually
  // capture a bug: Snipping Tool, then Ctrl+V. Without this they have to save
  // the image to disk first, which most will not bother doing.
  const handlePaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (pasted.length) {
      e.preventDefault();
      acceptFiles(pasted);
      toast.success(`Pasted ${pasted.length} screenshot(s).`);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    // Only guards against an empty submission. Not a quality bar: an earlier
    // 5/10 character minimum rejected a perfectly reasonable "test".
    if (!form.title.trim()) {
      toast.error("Please enter a title.");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Please enter a description.");
      return;
    }
    if (groups.length > 0 && !form.groupId) {
      toast.error("Please select an area.");
      return;
    }
    if (priorities.length > 0 && !form.priority) {
      toast.error("Please select a priority.");
      return;
    }

    setSubmitting(true);
    try {
      const report = await createErrorReport({
        ...form,
        pageUrl: pageUrlRef.current,
        browserInfo: collectBrowserInfo(),
        files,
      });
      toast.success(`Reported. Reference ${report.reference_number}`);
      onSubmitted?.(report);
      onClose?.();
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        data?.detail ||
        (Array.isArray(data?.files) && data.files[0]) ||
        (Array.isArray(data?.title) && data.title[0]) ||
        (Array.isArray(data?.description) && data.description[0]) ||
        "Could not send the report. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const groups = options?.groups || [];
  const priorities = options?.priorities || [];
  const boards = options?.boards || [];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center bg-black/40 p-3 sm:p-4 overflow-y-auto"
      // Stop a stray drop navigating the browser away from the CRM.
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <form
        onSubmit={submit}
        onPaste={handlePaste}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <AlertCircle size={18} style={{ color: BRAND }} />
            <h2 className="text-lg font-medium text-gray-800">Report an issue</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Say up front when this environment will save but not post, so the
              status never comes back as a surprising "Not sent". */}
          {options && !loadingOptions && options.will_post === false && (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
              {options.will_post_reason ||
                "This report will be saved but not posted to Monday from this environment."}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What went wrong? <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Short summary, e.g. Buyers Match shows Anonymous instead of the name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              maxLength={255}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5}
              placeholder="What were you doing, what did you expect, and what happened instead?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            />
          </div>

          {loadingOptions ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              Loading options from Monday...
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {boards.length > 1 && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Board
                  </label>
                  <select
                    value={form.boardId}
                    onChange={(e) => changeBoard(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                  >
                    {boards.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {groups.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={form.groupId}
                    onChange={(e) => setForm({ ...form, groupId: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                  >
                    <option value="">--Select--</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {priorities.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                  >
                    <option value="">--Select--</option>
                    {sortPriorities(priorities).map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Starting status and Assign to are deliberately NOT shown.
                  Reporters should not be triaging: every issue goes in as
                  Pending and unassigned, and whoever owns the board decides.
                  The server applies those defaults, so nothing is lost by
                  leaving the controls out. */}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Screenshots
            </label>
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setDropActive(true);
              }}
              onDragLeave={() => setDropActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDropActive(false);
                acceptFiles(e.dataTransfer?.files);
              }}
              className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg py-6 px-4 cursor-pointer transition-colors ${
                dropActive
                  ? "border-[#55C5D0] bg-[#eaf7f8]"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <Upload size={20} className="text-gray-400" />
              <span className="text-sm text-gray-600">
                Drag images here, paste with Ctrl+V, or click to choose
              </span>
              <span className="text-xs text-gray-400">
                Up to {MAX_FILES} files, {MAX_FILE_MB} MB each
              </span>
              <input
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  acceptFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>

            {files.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3">
                {files.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} className="relative group w-24">
                    <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                      {previews[idx] ? (
                        <img
                          src={previews[idx]}
                          alt={f.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-[10px] text-gray-500 px-1 text-center break-all">
                          {f.name}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500">
            The page you were on and your browser details are attached
            automatically, and the report is sent under your name.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg text-white flex items-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: BRAND }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={14} />
                Send report
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportIssueModal;
